import { Fragment } from 'react';
import { parseMarkdown, type Block, type Span } from '@st/markdown';
import { cx } from '../lib/cx';

/**
 * Tijelo članka.
 *
 * Ulaz je Markdown iz baze, izlaz su NAŠI elementi — nikad `dangerouslySetInnerHTML`.
 * Parser (`@st/markdown`) vraća stablo blokova, ovdje se svakom bloku pridružuje
 * tipografija. Zato tekst iz baze ne može postati markup ni onda kad netko u
 * admin polje zalijepi `<script>`.
 *
 * Mjera retka je ~68 znakova (`max-w-[68ch]`): duži red oko ove veličine slova
 * tjera oko da traži početak sljedećeg.
 */

function Inline({ spans }: { spans: readonly Span[] }) {
  return (
    <>
      {spans.map((s, i) => {
        if (s.kind === 'link') {
          return (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noreferrer noopener"
              className="text-accent-dark underline decoration-accent/40 underline-offset-[3px] transition-colors hover:decoration-accent-dark"
            >
              {s.text}
            </a>
          );
        }
        if (s.code) {
          return (
            <code
              key={i}
              className="rounded-sm bg-bg-alt px-1.5 py-0.5 font-mono text-[0.9em] text-ink"
            >
              {s.text}
            </code>
          );
        }
        if (s.bold) {
          return (
            <strong key={i} className="font-semibold text-ink">
              {s.text}
            </strong>
          );
        }
        if (s.italic) {
          return (
            <em key={i} className="italic">
              {s.text}
            </em>
          );
        }
        return <Fragment key={i}>{s.text}</Fragment>;
      })}
    </>
  );
}

function BlockView({ block, lead }: { block: Block; lead: boolean }) {
  switch (block.kind) {
    case 'heading':
      return block.level === 2 ? (
        /*
          Naslov drugog reda nosi tanku crtu iznad sebe umjesto samo praznog
          prostora. U dugom tekstu razmak sam ne odvaja dovoljno jasno — čitatelj
          koji preskače kroz članak treba vidjeti gdje jedan dio staje.
        */
        <h2 className="mt-10 border-t border-line-subtle pt-7 font-display text-[24px] font-semibold leading-tight text-ink first:mt-0 first:border-0 first:pt-0">
          <Inline spans={block.spans} />
        </h2>
      ) : (
        <h3 className="mt-7 font-display text-[18.5px] font-semibold leading-snug text-ink">
          <Inline spans={block.spans} />
        </h3>
      );

    case 'paragraph':
      return (
        <p
          className={cx(
            'text-ink',
            // Uvodni odlomak je krupniji i svjetlijeg tona — ulaz u tekst.
            lead ? 'text-[18px] leading-[1.65] text-ink-muted' : 'text-[16px] leading-[1.75]',
          )}
        >
          <Inline spans={block.spans} />
        </p>
      );

    case 'list':
      return block.ordered ? (
        /*
          Brojevi su vlastiti (`counter`), ne `list-decimal`: u koraku „1. Struk —
          u najužem dijelu" broj mora biti u boji naglaska i poravnat s prvim
          retkom teksta, a ne siva znamenka koju crta preglednik.
        */
        <ol className="flex list-none flex-col gap-3 [counter-reset:st]">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="relative pl-9 text-[16px] leading-[1.75] text-ink [counter-increment:st] before:absolute before:left-0 before:top-[3px] before:flex before:h-[22px] before:w-[22px] before:items-center before:justify-center before:rounded-pill before:bg-accent-light before:text-[12px] before:font-semibold before:text-accent-deep before:tabular-nums before:content-[counter(st)]"
            >
              <Inline spans={item} />
            </li>
          ))}
        </ol>
      ) : (
        <ul className="flex list-none flex-col gap-2.5">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="relative pl-6 text-[16px] leading-[1.75] text-ink before:absolute before:left-[3px] before:top-[11px] before:h-[5px] before:w-[5px] before:rounded-full before:bg-accent"
            >
              <Inline spans={item} />
            </li>
          ))}
        </ul>
      );

    case 'quote':
      return (
        <blockquote className="border-l-2 border-accent bg-bg-alt/60 py-3 pl-5 pr-4 text-[16px] italic leading-[1.7] text-ink-muted">
          <Inline spans={block.spans} />
        </blockquote>
      );

    /*
      Portal ima širine za prave stupce, pa tablica ostaje tablica. Vodoravno
      klizanje nosi omotač, ne stranica — inače bi duga tablica gurala cijeli
      članak ustranu. Brojčani stupci idu `tabular-nums` da se znamenke poravnaju.
    */
    case 'table':
      return (
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full border-collapse text-[15px] leading-[1.6]">
            <thead>
              <tr>
                {block.header.map((cell, i) => (
                  <th
                    key={i}
                    scope="col"
                    className="border-b border-line px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-[0.08em] text-ink-muted first:pl-0 last:pr-0"
                  >
                    <Inline spans={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-b border-line-subtle last:border-0">
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={cx(
                        'px-3 py-2.5 align-top text-ink first:pl-0 last:pr-0',
                        j === 0 ? 'font-semibold' : 'tabular-nums',
                      )}
                    >
                      <Inline spans={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'rule':
      return <hr className="border-line-subtle" />;
  }
}

export function Prose({ body, className }: { body: string | null; className?: string }) {
  const blocks = parseMarkdown(body);
  if (blocks.length === 0) return null;

  const firstParagraph = blocks.findIndex((b) => b.kind === 'paragraph');

  return (
    <div className={cx('flex max-w-[68ch] flex-col gap-5', className)}>
      {blocks.map((block, i) => (
        <BlockView key={i} block={block} lead={i === firstParagraph} />
      ))}
    </div>
  );
}
